import hoistNonReactStatics from "hoist-non-react-statics";
import {
  Children,
  type ReactElement,
  type ReactNode,
  forwardRef,
  isValidElement,
} from "react";
import type { GestureResponderEvent } from "react-native";

import { isDev, isLocal } from "@/config/env";
import { useAnalytics, useEventContext } from "@/hooks/analytics";

type TrackableProps = {
  "aria-label"?: string;
  text?: string;
  label?: string | ReactElement;
  dataTrackingId?: string;
  placeholder?: string;
  children?: ReactNode;
  onPress?: (e: GestureResponderEvent) => void;
  onSubmitEditing?: (e: GestureResponderEvent) => void;
  onValueChange?: (value: string) => void;
  labels?: { [key: string]: string };
  options?: { [key: string]: string };
  extraTrackingContext?: { [key: string]: string };
};

type TrackableEventName = "onPress" | "onSubmitEditing" | "onValueChange";
class TrackableEvent {
  propName: TrackableEventName;
  interaction: string;

  constructor(propName: TrackableEventName, interaction: string) {
    this.propName = propName;
    this.interaction = interaction;
  }
}

const EVENTS_BY_ELEMENT = Object.freeze({
  button: [new TrackableEvent("onPress", "clicked")],
  link: [new TrackableEvent("onPress", "clicked")],
  textArea: [new TrackableEvent("onSubmitEditing", "submitted")],
  radioGroup: [new TrackableEvent("onValueChange", "selected")],
  toggleGroup: [new TrackableEvent("onValueChange", "selected")],
});

type ElementType = keyof typeof EVENTS_BY_ELEMENT;

const singleSelectExtension = (
  selectedValue: string,
  props: TrackableProps,
) => ({
  accessibleName: props.options?.[selectedValue],
  option: selectedValue,
});

const trackingExtension = {
  radioGroup: singleSelectExtension,
  toggleGroup: singleSelectExtension,
};

/** Type guard otherwise trackingExtension[elementType] fails due to only having radioGroup */
const hasTrackingExtension = (
  elementType: ElementType,
): elementType is keyof typeof trackingExtension => {
  return elementType in trackingExtension;
};

const strRepComplexChildren = (children: ReactNode): string => {
  let label = "";
  Children.forEach(children, (child: ReactNode) => {
    if (typeof child === "string") {
      label += ` ${child}`;
    } else if (isValidElement(child)) {
      const element = child as ReactElement<{ children?: ReactNode }>;
      if (element.props.children) {
        label += strRepComplexChildren(element.props.children);
      }
    }
  });
  return label;
};

const WHITESPACE_STARTER = new RegExp(/^\s*/);
const SENTENCE_ENDER = new RegExp(/\.\s*$/);
/**
 * @param props Raw props passed to the interactable element
 * @param detailed If false, prefers explicitly provided dataTrackingId for high-level tracking.
 *                 Otherwise, uses accessible name derived from its copy.
 */
export const accessibleNameFromProps = (
  props: TrackableProps,
  detailed: boolean,
) => {
  if (!detailed && props.dataTrackingId) return props.dataTrackingId;
  let name =
    [props["aria-label"], props.text, props.label, props.placeholder].filter(
      (maybeString) => typeof maybeString === "string",
    )[0] || strRepComplexChildren(props.children);
  if (!name && isValidElement(props.label)) {
    name = strRepComplexChildren(props.label);
  }
  return name
    .replace(SENTENCE_ENDER, "")
    .replace(WHITESPACE_STARTER, "")
    .replace(/^\s+|\s+$/g, "");
};

const toKebabCase = (str: string) => {
  return (str || "")
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/\s+/g, "-")
    .toLowerCase();
};

export const useTrackedProps = (props: any, elementType: ElementType) => {
  const { track } = useAnalytics();
  const { location, conversationId, pathname } = useEventContext();

  // Strip tracking props so they don't get forwarded to DOM elements
  const { dataTrackingId: _, extraTrackingContext: __, ...finalProps } = props;

  // Truthify various props to support different interaction types (e.g. anchor tags, text areas)
  if (elementType === "link") {
    finalProps.onPress = (e: any) => props.onPress?.(e);
  } else if (elementType === "textArea") {
    finalProps.onSubmitEditing = (e: any) => props.onSubmitEditing?.(e);
  }

  const eventsToTrack = EVENTS_BY_ELEMENT[elementType].filter(
    (event) => event.propName in finalProps,
  );
  if (!eventsToTrack.length) return finalProps;

  eventsToTrack.forEach((trackableEvent) => {
    finalProps[trackableEvent.propName] = (...args: any[]) => {
      const extendedTrackingContext =
        hasTrackingExtension(elementType) && typeof args[0] === "string"
          ? trackingExtension[elementType](args[0], props)
          : undefined;
      const interactableElementName = accessibleNameFromProps(props, false);
      if ((isLocal || isDev) && !interactableElementName) {
        console.error(
          `[withTracking] Tracked ${elementType} has no accessible name. Add an aria-label, text, or dataTrackingId prop.`,
        );
      }
      const eventName = `superclinical-${location}__${trackableEvent.interaction}-${toKebabCase(interactableElementName)}`;
      track(eventName, {
        event_context: {
          pathname,
          location,
          conversationId,
          accessibleName: accessibleNameFromProps(props, true),
          ...extendedTrackingContext,
          ...props.extraTrackingContext,
        },
      });
      props[trackableEvent.propName]?.(...args);
    };
  });

  return finalProps;
};

/**
 * @returns A Component that will automatically track events on the given element type, and which accepts an optional dataTrackingId prop to aggregate events at a higher level than their copy.
 */
export default function withTracking(
  Component: React.ComponentType<any>,
  elementType: ElementType,
): React.ComponentType<any> {
  const WrappedComponent = forwardRef<any, { untracked?: boolean }>(
    (props, ref) => {
      const trackedProps = useTrackedProps(props, elementType);
      if (props.untracked) return <Component ref={ref} {...props} />;
      return <Component ref={ref} {...trackedProps} />;
    },
  );

  hoistNonReactStatics(WrappedComponent, Component);
  WrappedComponent.displayName = `withTracking(${Component.displayName || Component.name || "UnnamedInteractableElement"})`;
  return WrappedComponent;
}
